import graphqlClient from "@/GQLClient";
import {
  SaveActivityDocument,
  DeleteActivityDocument,
  type ActivityInput,
  GetInternalActivitiesOnMyInstitutionDocument,
  type ActivityFragment,
  GetSummarizedInternalActivitiesDocument,
  type SummarizedActivitiesFragment,
  Delete_Type,
  GetExternalActivitiesDocument,
  Module_Type,
  GetSummarizedExternalActivitiesDocument,
  type EntityPagerFilter,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import type { IPagerResult } from "@/interfaces";
import type { IdType } from "@/types";

const getActivities = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<SummarizedActivitiesFragment>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedInternalActivitiesDocument,
    variables: {
      filter,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.institutions?.getMyInstitution.activities) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.activities;
    return {
      edges: edges.map((item) => item.node as SummarizedActivitiesFragment),
      pageInfo,
    };
  }

  throw new SentryGraphQLError(`api.activities.getActivities`, response);
};

const getActivityDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetInternalActivitiesOnMyInstitutionDocument,
    variables: {
      filter: {
        first: 1,
        ids,
      },
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.institutions?.getMyInstitution.activities) {
    const { edges } = response.data.institutions.getMyInstitution.activities;
    return edges.map((item) => item.node as ActivityFragment).at(0);
  }

  throw new SentryGraphQLError(`api.activities.getActivityDetail`, response);
};

const getExtarnalActivities = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<SummarizedActivitiesFragment>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedExternalActivitiesDocument,
    variables: {
      filter,
      type: Module_Type.Activity,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.externalContent;
    return {
      edges: edges.map((item) => item.node as SummarizedActivitiesFragment),
      pageInfo,
    };
  }

  throw new SentryGraphQLError(
    `api.activities.getExtarnalActivities`,
    response,
  );
};

const getExternalActivityDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetExternalActivitiesDocument,
    variables: {
      type: Module_Type.Activity,
      filter: {
        first: 1,
        ids,
      },
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges } =
      response.data.institutions.getMyInstitution.externalContent;
    return edges.map((item) => item.node as ActivityFragment).at(0);
  }

  throw new SentryGraphQLError(
    `api.activities.getExternalActivityDetail`,
    response,
  );
};

const save = async (activity: ActivityInput) => {
  const response = await graphqlClient.query({
    query: SaveActivityDocument,
    variables: {
      activity,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.activity?.save?.success) {
    return response;
  }

  throw new SentryGraphQLError(`api.activities.save`, response);
};

const deleteItem = async (id: string, type: Delete_Type) => {
  const response = await graphqlClient.query({
    query: DeleteActivityDocument,
    variables: {
      id,
      type,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.activity?.delete?.success) {
    return response;
  }

  throw new SentryGraphQLError(`api.activities.delete`, response);
};

export default {
  getActivities,
  getActivityDetail,
  getExtarnalActivities,
  getExternalActivityDetail,
  save,
  deleteItem,
};
